/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import {
    Announced,
    classNamesFunction,
    DefaultButton,
    FontWeights,
    getId,
    IButtonProps,
    IButtonStyles,
    IconButton,
    IIconStyleProps,
    IIconStyles,
    IMessageBarStyleProps,
    IMessageBarStyles,
    IScrollablePaneStyleProps,
    IScrollablePaneStyles,
    IStackProps,
    Link,
    MessageBar,
    MessageBarType,
    Panel,
    PanelType,
    PrimaryButton,
    ScrollablePane,
    Stack,
    Sticky,
    StickyPositionType
} from '@fluentui/react';
import { Property } from 'csstype';
import { observer } from 'mobx-react';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { useHistory, useLocation } from 'react-router';
import { BasicModal } from 'src/core/components/BasicModal';
import { EditableTitle } from 'src/core/components/EditableTitle';
import { EmptyStateView } from 'src/core/components/EmptyStateView';
import { ErrorTextContainer } from 'src/core/components/ErrorTextContainer';
import { ExternalLink } from 'src/core/components/ExternalLink';
import { LuisText } from 'src/core/components/LuisText';
import { Page } from 'src/core/components/Page';
import { useDocumentEventHandler } from 'src/core/hooks/useDocumentEventHandler';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { usePrevious } from 'src/core/hooks/usePrevious';
import { useProgressTracker } from 'src/core/hooks/useProgressTracker';
import { useResponsiveMode } from 'src/core/hooks/useResponsiveMode';
import { useSelection } from 'src/core/hooks/useSelection';
import { logButtonClick, logDocumentLanguage } from 'src/core/services/telemetry/eventsNamesTemplate';
import { ProgressTrackerStore } from 'src/core/stores/ProgressTrackerStore';
import { getScopedTheme, getStylistV2 } from 'src/core/themes/luisStylist';
import { HistoryStacks } from 'src/core/types/HistoryStacks';
import { mapUserRoleToUserActions } from 'src/core/utils/azureUtils';
import { FeatureToggles } from 'src/core/utils/featureToggles';
import { LuisKeyCodes } from 'src/core/utils/keyCodes';
import { LuisConstants } from 'src/core/utils/LuisConstants';
import { routeTable } from 'src/core/utils/routeTable';
import { getPortalFromUrl } from 'src/core/utils/routingUtils';
import { fileViewToOptionName } from 'src/core/utils/telemetryUtils';
import { AllFilesView } from 'src/customText/appShell/dataTaggingPage/components/allFilesView/AllFilesView';
import { CustomTextAzureMLTab } from 'src/customText/appShell/dataTaggingPage/components/CustomTextAzureMLTab';
import { CustomTextChooseDataset } from 'src/customText/appShell/dataTaggingPage/components/CustomTextChooseDataset';
import { CustomTextClassPalette } from 'src/customText/appShell/dataTaggingPage/components/CustomTextClassPalette';
import { CustomTextDataDistribution } from 'src/customText/appShell/dataTaggingPage/components/CustomTextDataDistribution';
import { CustomTextModelGuidanceRecommendations } from 'src/customText/appShell/dataTaggingPage/components/CustomTextModelGuidanceRecommendations';
import { DocumentLoadingView, DocumentView } from 'src/customText/appShell/dataTaggingPage/components/DocumentView';
import { FileView } from 'src/customText/appShell/dataTaggingPage/components/FilesViewDropdown';
import { SamplingCommandBar } from 'src/customText/appShell/dataTaggingPage/components/SamplingCommandBar';
import { SamplingFooter } from 'src/customText/appShell/dataTaggingPage/components/SamplingFooter';
import { SavingIndicator } from 'src/customText/appShell/dataTaggingPage/components/SavingIndicator';
import { TrainingLimitsUI } from 'src/customText/appShell/dataTaggingPage/components/TrainingLimitsUI';
import { useFetchDocument } from 'src/customText/appShell/dataTaggingPage/hooks/useFetchDocument';
import { useSaveChanges } from 'src/customText/appShell/dataTaggingPage/hooks/useSaveChanges';
import { DocumentsFilter, FileFilter, SamplerStore } from 'src/customText/appShell/dataTaggingPage/samplerStore';
import { ModelPerformanceModalDto } from 'src/customText/appShell/modelDetailsPages/components/TestDocumentDetailsModal';
import { CustomTextBreadcrumb } from 'src/customText/core/components/CustomTextBreadcrumb';
import { CustomTextClassData } from 'src/customText/core/stores/CustomTextClassStore';
import { CustomTextDocumentMetadata } from 'src/customText/core/stores/CustomTextDocumentStore';
import { CustomTextEntityLabelStore } from 'src/customText/core/stores/CustomTextEntityLabelStore';
import { customTextTeachingBubbleMessages } from 'src/customText/core/utils/customTextTeachingBubbleMessages';
import { AutoLabelingModal } from 'src/customText/verticals/autotagTriggerModal/components/AutoLabelingModal';
import { LabelingMode } from 'src/customText/verticals/extractionLabeler/ExtractionLabeler';
import { FilesViewer } from 'src/customText/verticals/extractionLabeler/FilesViewer';
import { createTeachingBubble, dequeueTeachingBubble } from 'src/luis/projects/events/utils/teachingBubbles';
import { DatasetType } from 'src/modules/modelTraining/types/trainingTypes';
import { EntityPalette, PalettePivotType } from 'src/verticals/EntityPalette';
import styled from 'styled-components';

const messages = defineMessages({
    saveNow: { id: 'customText.customTextDataTaggingPage.saveNow', defaultMessage: 'Save now' },
    pageTitle: { id: 'customText.customTextDataTaggingPage.pageTitle', defaultMessage: 'Data labeling' },
    taggingPaletteHeader: { id: 'customText.customTextDataTaggingPage.taggingPaletteHeader', defaultMessage: 'Activity pane' },
    inprogressMessage: { id: 'customText.customTextDataTaggingPage.inprogressMessage', defaultMessage: 'Saving current changes ...' },
    expandTaggingPalette: { id: 'customText.customTextDataTaggingPage.expandTaggingPalette', defaultMessage: 'Expand tagging palette' },
    collapseTaggingPalette: {
        id: 'customText.customTextDataTaggingPage.collapseTaggingPalette',
        defaultMessage: 'Collapse tagging palette'
    },
    useAmlToLabel: {
        id: 'customText.customTextDataTaggingPage.useAmlToLabel',
        defaultMessage: 'use Azure Machine Learning to label'
    },
    extractionSubTitle: {
        id: 'customText.customTextDataTaggingPage.extractionSubTitle',
        defaultMessage: 'Select a document to annotate its text with entity labels or'
    },
    subTitleCont: {
        id: 'customText.customTextDataTaggingPage.extractionSubTitleCont',
        defaultMessage:
            " After labeling the documents and adding them to training or testing sets, you'll be ready to create a model with this data in"
    },
    singleClassSubTitle: {
        id: 'customText.customTextDataTaggingPage.singleClassSubTitle',
        defaultMessage: 'Select a document to categorize it into a class or'
    },
    multiClassSubTitle: {
        id: 'customText.customTextDataTaggingPage.multiClassSubTitle',
        defaultMessage: 'Select a document to categorize it into any number of classes or'
    },
    extractionSubTitleTemp: {
        id: 'customText.customTextDataTaggingPage.extractionSubTitleTemp',
        defaultMessage:
            "Select a document to annotate its text with entity labels. After labeling the documents and adding them to training or testing sets, you'll be ready to create a model with this data in"
    },
    singleClassSubTitleTemp: {
        id: 'customText.customTextDataTaggingPage.singleClassSubTitleTemp',
        defaultMessage:
            "Select a document to categorize it into a class. After labeling the documents and adding them to training or testing sets, you'll be ready to create a model with this data in"
    },
    multiClassSubTitleTemp: {
        id: 'customText.customTextDataTaggingPage.multiClassSubTitleTemp',
        defaultMessage:
            "Select a document to categorize it into any number of classes. After labeling the documents and adding them to training or testing sets, you'll be ready to create a model with this data in"
    },
    trainingLink: {
        id: 'customText.customTextDataTaggingPage.trainingLink',
        defaultMessage: 'Training jobs'
    },
    noLabelsMessage: {
        id: 'customText.customTextDataTaggingPage.noLabelsMessage',
        defaultMessage: 'Add labels first to your document to activate the datasets options'
    },
    noTaggedDocumentsTitle: {
        id: 'customText.customTextDataTaggingPage.noTaggedDocumentsTitle',
        defaultMessage: `You don't have any labeled documents.`
    },
    noDocumentsMatchingFilterTitle: {
        id: 'customText.customTextDataTaggingPage.noDocumentsMatchingFilterTitle',
        defaultMessage: `No results for the selected filter. Remove filter to proceed.`
    },
    noDocumentsFound: {
        id: 'customText.customTextDataTaggingPage.noDocumentsFound',
        defaultMessage: `You don't have any documents in your storage.`
    },
    notFoundFileSubTitle: {
        id: 'customText.customTextDataTaggingPage.notFoundFileSubTitle',
        defaultMessage: "Document {name} couldn't be found."
    },
    emptyStorageTitle: {
        id: 'customText.customTextDataTaggingPage.emptyStorageTitle',
        defaultMessage: 'Your storage container "{containerName}" is empty.'
    },
    emptyStorageSubTitle: {
        id: 'customText.customTextDataTaggingPage.emptyStorageSubTitle',
        defaultMessage: 'Please upload some files and refresh the page.'
    },
    changeFilterPromptTitle: {
        id: 'customText.customTextDataTaggingPage.changeFilterPromptTitle',
        defaultMessage: 'You have unsaved changes.'
    },
    changeFilterPromptBody: {
        id: 'customText.customTextDataTaggingPage.changeFilterPromptBody',
        defaultMessage: 'Some changes have not been saved, please save your work before changing the filter.'
    },
    disabledAutoSavingWarning: {
        id: 'customText.customTextDataTaggingPage.disabledAutoSavingWarning',
        defaultMessage: `When filters are applied, automatic saving won't work and you will need to manually save your labels.`
    },
    labelingLimitWarning: {
        id: 'customText.customTextDataTaggingPage.labelingLimitWarning',
        defaultMessage: `Max allowed label is {threshold} characters per label.`
    },
    modelPerformanceMessage: {
        id: 'customText.customTextDataTaggingPage.modelPerformanceMessage',
        defaultMessage: 'Go back to Model performance to resume viewing model details'
    },
    modelPerformanceButton: {
        id: 'customText.customTextDataTaggingPage.modelPerformanceButton',
        defaultMessage: 'Return to model performance'
    },
    labelDisabledMessageBar: {
        id: 'customText.customTextDataTaggingPage.labelDisabledMessageBar',
        defaultMessage:
            'Labeling in Language Studio is disabled for this project, but you can still train a model, view model performance, or deploy a model. To switch labeling activity back to Language Studio, go to the'
    },
    labelDisabledMessageBarExtLink: {
        id: 'customText.customTextDataTaggingPage.labelDisabledMessageBarExtLink',
        defaultMessage: 'Azure Machine Learning'
    },
    labelDisabledMessageBarCont: {
        id: 'customText.customTextDataTaggingPage.labelDisabledMessageBarCont',
        defaultMessage: 'tab in the Activity pane.'
    },
    nerDeleteWarningModalTitle: {
        id: 'customText.customTextDataTaggingPage.nerDeleteWarningModalTitle',
        defaultMessage: 'There must be at least one entity created in your project'
    },
    classificationDeleteWarningModalTitle: {
        id: 'customText.customTextDataTaggingPage.classificationDeleteWarningModalTitle',
        defaultMessage: 'There must be at least two classes created in your project'
    },
    nerDeleteWarningModalContent: {
        id: 'customText.customTextDataTaggingPage.nerDeleteWarningModalContent',
        defaultMessage: 'Because your project is connected with Azure Machine Learning, at least one entity are required at all times.'
    },
    classificationDeleteWarningModalContent: {
        id: 'customText.customTextDataTaggingPage.classificationDeleteWarningModalContent',
        defaultMessage:
            'Because your project is connected with Azure Machine Learning, at least two classes are required at all times. If you want only one class, disconnect the project from Azure Machine Learning.'
    },
    gotIt: {
        id: 'customText.customTextDataTaggingPage.gotIt',
        defaultMessage: 'Got it'
    },
    cancel: {
        id: 'customText.customTextDataTaggingPage.cancel',
        defaultMessage: 'Cancel'
    }
});

const { styleComponent, styleDiv } = getStylistV2('DataTaggingPage');
const theme = getScopedTheme('CustomTextDataTaggingPage');
const ScrollablePaneWrapper = styleDiv('ScrollablePaneWrapper', { flex: 1, position: 'relative' });
const ContentWrapper = styleComponent<IStackProps>(Stack)('DataTaggingPageContentWrapper', { overflow: 'hidden' });

const TaggingPaletteHeader = styled(LuisText)({
    padding: '8px 0 0 16px !important',
    fontWeight: 600,
    fontSize: '20px'
});

const collapseButtonStyles = classNamesFunction<IButtonProps, IButtonStyles>()({
    root: { width: 44, height: 44 }
});

const collapseIconStyles = classNamesFunction<IIconStyleProps, IIconStyles>()({
    root: { fontSize: 10, color: theme.collapseIconColor }
});

const containerScrollablePaneStyles = classNamesFunction<IScrollablePaneStyleProps, IScrollablePaneStyles>()({
    contentContainer: { display: 'flex', flexDirection: 'column', overflowX: 'hidden' }
});

const ModelPerformanceMessageBarStyles = classNamesFunction<IMessageBarStyleProps, IMessageBarStyles>()({
    root: {
        width: '100%'
    },
    innerText: {
        width: '100%'
    }
});

const PaletteContainer = styled(Stack)<{ isCollapsed: boolean; width: string; minWidth: string }>(props => ({
    width: props.isCollapsed ? '44px' : props.width,
    minWidth: props.isCollapsed ? null : props.minWidth
}));

export const CustomTextDataTaggingPage = observer(() => {
    const history = useHistory();
    const location = useLocation();
    const { localize } = useLocalization();
    const { serviceName } = getPortalFromUrl();
    const { dispatcher, rootStore, uiStore } = useGlobalState();
    const { isXXLargeOrGreater, isLargeOrGreater, isMediumOrGreater } = useResponsiveMode();

    const { customTextCultureStorehouse } = rootStore;
    const currentProjectStore = rootStore.currentCustomTextProjectStore;
    const selectedModelInRecommendation = location?.state as { model: string; cardKey: string };
    const {
        projectKind,
        amlProjectPath,
        isLabelingLocked,
        gptPredictionsStore,
        currentDocumentStore,
        id: currentProjectId,
        trainingJobStoreHouse,
        successfullyTrainedModel,
        autoTaggingJobStoreHouse,
        currentRunningAutoTaggingJob
    } = currentProjectStore;
    const selectedPalettePivot = amlProjectPath ? 'azureML' : (location?.state as { tab: PalettePivotType })?.tab ?? 'tags';
    const currentDocumentGptData = gptPredictionsStore.documents.find(d => d.name === currentDocumentStore?.name);

    const [samplerStore] = React.useState(() => new SamplerStore(gptPredictionsStore));
    const [deleteWarningModalHidden, setDeleteWarningModalHidden] = React.useState(true);
    const [labelingMode, setLabelingMode] = React.useState<LabelingMode>({ mode: 'menu' });
    const [isChangeFilterPromptHidden, setIsChangeFilterPromptHidden] = React.useState(true);
    const filesFilterApplied = (location.state as { filter: FileFilter })?.filter;
    const [modelPerformanceInfo] = React.useState(location.state as ModelPerformanceModalDto);
    const [isAutotagTriggerModalHidden, setIsAutotagTriggerModalHidden] = React.useState(true);
    const [isLabelingWarningVisible, setIsLabelingWarningVisible] = React.useState<boolean>(false);
    const [isTaggingPaletteHeaderCollapsed, setIsTaggingPaletteHeaderCollapsed] = React.useState(false);
    const [taggingPaletteOpenPivot, setTaggingPaletteOpenPivot] = React.useState<PalettePivotType>(selectedPalettePivot);
    const [isModelPerformanceMessageHidden, setIsModelPerformanceMessageHidden] = React.useState(!modelPerformanceInfo?.isModalOpen);
    const [labelingHistoryStacks] = React.useState<HistoryStacks<CustomTextEntityLabelStore[]>>(() => ({
        undo: [],
        redo: []
    }));
    const [tableAndPaletteWidthPercentage, setTableAndPaletteWidthPercentage] = React.useState({
        tableWidth: '76.5%',
        paletteWidth: '23.5%'
    });

    const searchParams = new URLSearchParams(location.search);
    const inSearchMode = searchParams.get('documentName');
    const paletteMinWidth = isLargeOrGreater ? '400px' : '160px';
    const decodedDocumentName = inSearchMode && decodeURIComponent(searchParams.get('documentName'));
    const prevDecodedDocumentName = usePrevious(decodedDocumentName);
    const hasNoTrainedModels = trainingJobStoreHouse.tracker.isCompleted && !successfullyTrainedModel.length;
    const isTriggerAutoTaggingJobDisabled =
        (!FeatureToggles.CustomTextGptLabeling && hasNoTrainedModels) ||
        !!currentRunningAutoTaggingJob ||
        !!autoTaggingJobStoreHouse.items.find(j => j.status === 'notStarted');

    const { canWrite } = mapUserRoleToUserActions(rootStore.serviceKey.userRole);
    const { selection, selectedItems } = useSelection<CustomTextDocumentMetadata>();
    const { savingState, onSave } = useSaveChanges(!Boolean(samplerStore.areFiltersApplied));
    const { isLoading: ifDocumentContentFetchingLoading, isFailed: isFetchingDocumentFailed } = useFetchDocument(
        samplerStore.currentDocument
    );

    const loadNextBatchTracker = useProgressTracker();
    const getSpecificFileTracker = useProgressTracker({ persistOnComplete: true });
    const goToNextFileTracker = useProgressTracker({ onComplete: () => samplerStore.next() });

    const isLoading = !currentProjectStore;
    const documents = currentProjectStore?.documents ?? [];
    const isExtractionProject = projectKind === 'CustomEntityRecognition';
    const entities = currentProjectStore?.extractionStore?.entities ?? [];
    const classes = currentProjectStore?.classificationStore?.classes ?? [];
    const documentCulture = currentDocumentStore?.language ?? currentProjectStore?.culture;
    const isSingleLabelClassificationProject = projectKind === 'CustomSingleLabelClassification';
    const isCurrentDocumentLoading =
        ifDocumentContentFetchingLoading || (currentDocumentStore && currentDocumentStore?.name !== samplerStore.currentDocument);
    const areAutoTagsControlsDisabled =
        isLabelingLocked ||
        (currentDocumentStore?.entityLabels.every(l => l.labelType !== 'autoTagged') &&
            (currentDocumentGptData?.entityLabels.length ?? 0) === 0);

    React.useEffect(() => {
        labelingHistoryStacks.redo = [];
        labelingHistoryStacks.undo = [];
    }, [currentDocumentStore]);

    React.useEffect(() => {
        if (filesFilterApplied) {
            if (filesFilterApplied === 'autotagged') {
                samplerStore.changeViewingOption('single');
            }
            samplerStore.changeAppliedFilters({ ...samplerStore.appliedFilters, files: [filesFilterApplied] });
        }
    }, [filesFilterApplied]);

    const navigateToDocument = (docName: string) => {
        if (docName !== modelPerformanceInfo?.documentName) {
            setIsModelPerformanceMessageHidden(true);
        }
        history.push(
            `${routeTable.customText.dataTaggingPage(
                currentProjectStore.id,
                currentProjectStore.projectKind
            )}?documentName=${encodeURIComponent(docName)}`
        );
    };

    const onChangeViewingOption = (option: FileView) => {
        if (savingState === 'dirty') {
            setIsChangeFilterPromptHidden(false);

            return;
        }
        selection.setAllSelected(false);
        samplerStore.changeViewingOption(option);

        logButtonClick(serviceName, 'DataLabeling', fileViewToOptionName(option), null, currentProjectId);
        history.push(routeTable.customText.dataTaggingPage(currentProjectStore.id, currentProjectStore.projectKind));
    };

    const onChangeAppliedFilters = (filters: DocumentsFilter) => {
        if (savingState === 'dirty') {
            setIsChangeFilterPromptHidden(false);

            return;
        }

        samplerStore.changeAppliedFilters(filters);
    };

    const onChangeDataset = (dataset: DatasetType) => {
        if (samplerStore.viewingOption === 'single') {
            dispatcher.dispatch('changeCurrentDocumentStoreDataset', { dataset });
        } else {
            dispatcher.dispatch('setDocumentsDataset', { documentNames: selectedItems.map(s => s.name), dataset });
            selection.setAllSelected(false);
        }
    };

    const getSelectedClassLabels = (): CustomTextClassData[] => {
        if (samplerStore.viewingOption === 'single') {
            return currentDocumentStore?.classLabels;
        }

        const classLabels: CustomTextClassData[] = [];
        selectedItems[0]?.classLabels.forEach(cl => {
            if (selectedItems.reduce((prev, curr) => prev && Boolean(curr.classLabels.find(l => l.id === cl.id)), true)) {
                classLabels.push(cl);
            }
        });
        return classLabels;
    };

    const noLabelsExist =
        (currentDocumentStore?.classLabels.length === 0 && currentDocumentStore?.entityLabels.length === 0) ||
        selectedItems?.some(d => d.classLabels.length === 0 && d.entityLabels.length === 0);

    const addEntityButtonIdRef = React.useRef<string>(getId());
    const autoLabelButtonIdRef = React.useRef<string>(getId());
    const trainingLimitsUIIdRef = React.useRef<string>(getId());
    const customTextPaletteIdRef = React.useRef<string>(getId());

    const TrainingLimitsHeader = <TrainingLimitsUI id={trainingLimitsUIIdRef.current} />;
    const taggingPaletteHeader = <TaggingPaletteHeader as="h2">{localize(messages.taggingPaletteHeader)}</TaggingPaletteHeader>;

    const isDatasetSelectionAllowed = () => {
        if (savingState === 'inProgress') {
            return true;
        }

        switch (samplerStore.viewingOption) {
            case 'single':
                return currentDocumentStore?.classLabels.length === 0 && currentDocumentStore?.entityLabels.length === 0;
            case 'all':
            default:
                return selectedItems.length === 0 || selectedItems.some(d => d.classLabels.length === 0 && d.entityLabels.length === 0);
        }
    };

    const getDatasetSelectionTooltip = () => {
        if (savingState === 'inProgress') {
            return localize(messages.inprogressMessage);
        }

        if (noLabelsExist) {
            return localize(messages.noLabelsMessage);
        }

        return null;
    };

    const taggingPaletteFooter = (
        <CustomTextChooseDataset
            onChangeSelection={onChangeDataset}
            viewingOption={samplerStore.viewingOption}
            tooltipMessage={getDatasetSelectionTooltip()}
            projectKind={currentProjectStore.projectKind}
            isSelectionDisabled={isDatasetSelectionAllowed()}
            currentSelection={
                samplerStore.viewingOption === 'all'
                    ? selectedItems.length === 1
                        ? selectedItems[0].documentDataset
                        : null
                    : currentDocumentStore?.documentDataset || 'Train'
            }
        />
    );

    const onRenderTaggingPaletteDistributionTab = () => (
        <CustomTextDataDistribution
            documents={currentProjectStore.documents}
            projectKind={currentProjectStore.projectKind}
            entities={currentProjectStore.extractionStore?.entities}
            classes={currentProjectStore.classificationStore?.classes}
            isInEmptyState={
                (currentProjectStore.extractionStore?.entities.length || 0) === 0 &&
                (currentProjectStore.classificationStore?.classes.length || 0) === 0
            }
        />
    );

    const onRenderRecommendationsTab = () => (
        <CustomTextModelGuidanceRecommendations
            isExtractionProject={isExtractionProject}
            selectedModelName={selectedModelInRecommendation?.model}
            cardKeyToNavigate={selectedModelInRecommendation?.cardKey}
        />
    );

    const onRenderAzureMLTab = () => <CustomTextAzureMLTab savingState={savingState} />;

    React.useEffect(
        () => () => {
            dequeueTeachingBubble('CustomTextDataTaggingPalette');
            dequeueTeachingBubble('CustomTextTrainingLimitsUI');
            dequeueTeachingBubble('CustomTextAddEntity');
        },
        []
    );

    React.useEffect(() => {
        if (
            canWrite &&
            uiStore.teachingBubbles.CustomTextViewingDropDown &&
            !uiStore.teachingBubbles.CustomTextDataTaggingPalette &&
            !uiStore.skippedTeachingBubbles.CustomTextViewingDropDown
        ) {
            createTeachingBubble(
                'CustomTextDataTaggingPalette',
                localize(customTextTeachingBubbleMessages.dataTaggingPaletteBody, {
                    paletteType: isExtractionProject ? 'entities' : 'classes'
                }),
                localize(customTextTeachingBubbleMessages.dataTaggingPaletteTitle),
                `#${customTextPaletteIdRef.current}`,
                true,
                true
            );

            return;
        }

        if (
            isExtractionProject &&
            uiStore.teachingBubbles.CustomTextDataTaggingPalette &&
            !uiStore.teachingBubbles.CustomTextAddEntity &&
            !uiStore.skippedTeachingBubbles.CustomTextDataTaggingPalette
        ) {
            createTeachingBubble(
                'CustomTextAddEntity',
                localize(customTextTeachingBubbleMessages.addEntityButtonBody),
                localize(customTextTeachingBubbleMessages.addEntityButtonTitle),
                `#${addEntityButtonIdRef.current}`,
                true,
                true
            );

            return;
        }

        if (
            ((uiStore.teachingBubbles.CustomTextAddClass && !uiStore.skippedTeachingBubbles.CustomTextAddClass) ||
                (uiStore.teachingBubbles.CustomTextAddEntity && !uiStore.skippedTeachingBubbles.CustomTextAddEntity)) &&
            !uiStore.teachingBubbles.CustomTextTrainingLimitsUI
        ) {
            createTeachingBubble(
                'CustomTextTrainingLimitsUI',
                localize(customTextTeachingBubbleMessages.trainingLimitsUIBody),
                localize(customTextTeachingBubbleMessages.trainingLimitsUITitle),
                `#${trainingLimitsUIIdRef.current}`,
                true,
                true
            );

            return;
        }

        if (
            FeatureToggles.CustomTextGptLabeling &&
            uiStore.teachingBubbles.CustomTextDataTaggingPalette &&
            (uiStore.teachingBubbles.CustomTextAddEntity || uiStore.teachingBubbles.CustomTextAddClass) &&
            !uiStore.skippedTeachingBubbles.CustomTextDataTaggingPalette
        ) {
            createTeachingBubble(
                'CustomTextAutoLabel',
                localize(customTextTeachingBubbleMessages.autoLabelButtonBody),
                localize(customTextTeachingBubbleMessages.autoLabelButtonTitle),
                `#${autoLabelButtonIdRef.current}`,
                true,
                true
            );
        }
    }, [canWrite, isExtractionProject, Object.values(uiStore.teachingBubbles)]);

    // sampler store -> URL
    React.useEffect(() => {
        if (samplerStore.currentDocument && samplerStore.viewingOption === 'single') {
            navigateToDocument(samplerStore.currentDocument);
        }
    }, [samplerStore.currentDocument, samplerStore.viewingOption]);

    React.useEffect(() => {
        if (decodedDocumentName !== null && decodedDocumentName !== prevDecodedDocumentName) {
            samplerStore.setCurrentDocument(decodedDocumentName);
        }
    }, [decodedDocumentName, prevDecodedDocumentName]);

    React.useEffect(() => {
        if (
            documents.length &&
            (samplerStore.documents.length !== documents.length || currentProjectStore.savingState.latestChangeDomain === 'documents')
        ) {
            samplerStore.setDocuments(documents);

            if (decodedDocumentName !== null && decodedDocumentName !== prevDecodedDocumentName) {
                samplerStore.setCurrentDocument(decodedDocumentName);
            }
        }
    }, [documents.length, currentProjectStore.savingState]);

    React.useEffect(() => {
        if (savingState === 'saved') {
            samplerStore.setDocuments(documents, true);
            setIsChangeFilterPromptHidden(true);
        }
    }, [savingState]);

    useDocumentEventHandler('keydown', event => {
        if (event.shiftKey && event.code.includes('Digit')) {
            const numKey = parseInt(event.code.match(/\d+/)[0] ?? '', 10);
            if (isNaN(numKey)) {
                return;
            }

            if (numKey === 0) {
                setLabelingMode({ mode: 'menu' });

                return;
            }

            if (numKey <= entities.length) {
                setLabelingMode({ entityId: entities[numKey - 1].id, mode: 'brush' });
            }
        }
        if (
            event.shiftKey &&
            (event.key === LuisKeyCodes.ArrowUp || event.key === LuisKeyCodes.ArrowDown) &&
            labelingMode.mode === 'brush'
        ) {
            const selectedEntityIndex = entities.findIndex(e => e.id === labelingMode.entityId);
            if (event.key === LuisKeyCodes.ArrowUp && selectedEntityIndex > 0) {
                event.preventDefault();
                setLabelingMode({ mode: 'brush', entityId: entities[selectedEntityIndex - 1].id });
            } else if (event.key === LuisKeyCodes.ArrowDown && selectedEntityIndex < entities.length - 1) {
                event.preventDefault();
                setLabelingMode({ mode: 'brush', entityId: entities[selectedEntityIndex + 1].id });
            }
        }
    });

    React.useEffect(() => {
        setIsTaggingPaletteHeaderCollapsed(!isMediumOrGreater);

        if (isXXLargeOrGreater) {
            setTableAndPaletteWidthPercentage({ tableWidth: '76.5%', paletteWidth: '23.5%' });
        } else if (!isMediumOrGreater) {
            setTableAndPaletteWidthPercentage({ tableWidth: '50%', paletteWidth: '50%' });
        } else {
            setTableAndPaletteWidthPercentage({ tableWidth: '76.5%', paletteWidth: '23.5%' });
        }
    }, [isMediumOrGreater, isXXLargeOrGreater]);

    const handleDeleteAmlWarning = () => {
        if (amlProjectPath && (entities.length === 1 || classes.length === 2)) {
            setDeleteWarningModalHidden(false);
            return false;
        }
        return true;
    };

    const handleAddEntity = (entityName: string) => dispatcher.dispatch('addNewExtractionEntity', { entityName, toastData: {} });

    const handleAddClass = (className: string) => dispatcher.dispatch('addNewClassificationClass', { className, toastData: {} });

    const handleDeleteEntity = (entityId: string) => {
        setLabelingMode({ mode: 'menu' });
        dispatcher.dispatch('removeExtractionEntities', { entitiesIds: [entityId], toastData: {} });
    };

    const handleDeleteClass = (classId: string) => {
        dispatcher.dispatch('removeClassificationClasses', { classesIds: [classId], toastData: {} });
    };

    const handleSubmitClasses = (classIds: string[]) => {
        if (samplerStore.viewingOption === 'all') {
            dispatcher.dispatch('submitClassificationClasses', {
                classIds,
                documentNames: selectedItems.map(s => s.name),
                toastData: {}
            });

            selection.setAllSelected(false);
        } else {
            dispatcher.dispatch('submitCurrentDocumentClassificationClasses', {
                classIds,
                toastData: {}
            });
        }
    };

    const getCultureName = (code: string) => customTextCultureStorehouse.items.find(item => item.code === code)?.name ?? '';

    const handleDocumentCultureChange = (culture: string) => {
        logDocumentLanguage(serviceName, 'DataLabeling', getCultureName(culture), currentProjectId);

        dispatcher.dispatch('changeDocumentCulture', { culture, toastData: {} });
    };

    const handleAcceptAllAutoTags = () => {
        if (!currentDocumentStore?.entityLabels.every(l => l.labelType !== 'autoTagged')) {
            dispatcher.dispatch('submitExtractionLabels', {
                labelingHistoryStacks,
                action: {
                    name: 'ConfirmPredictions',
                    labels: currentDocumentStore.entityLabels
                        .filter(l => l.labelType === 'autoTagged')
                        .map(label => ({
                            start: label.startCharIndex,
                            end: label.endCharIndex,
                            entityId: label.entityId
                        }))
                },
                toastData: {}
            });
            logButtonClick(serviceName, 'DataLabeling', 'AcceptAllAutolabels', null, currentProjectId);
        }

        if (currentDocumentGptData?.entityLabels.length) {
            dispatcher.dispatch('submitGptLabels', { labelingHistoryStacks, labels: currentDocumentGptData.entityLabels, toastData: {} });
            logButtonClick(serviceName, 'DataLabeling', 'AcceptAllGPTlabels', null, currentProjectId);
        }

        if (currentDocumentGptData?.classLabels.length) {
            dispatcher.dispatch('submitGptClassificationLabels', {
                classIds: currentDocumentGptData?.classLabels.map(c => c.id),
                toastData: {}
            });
            logButtonClick(serviceName, 'DataLabeling', 'AcceptAllGPTlabels', null, currentProjectId);
        }
    };

    const handleRejectAllAutoTags = () => {
        if (!currentDocumentStore?.entityLabels.every(l => l.labelType !== 'autoTagged')) {
            dispatcher.dispatch('submitExtractionLabels', {
                labelingHistoryStacks,
                action: {
                    name: 'RemoveLabels',
                    labels: currentDocumentStore.entityLabels
                        .filter(l => l.labelType === 'autoTagged')
                        .map(label => ({
                            start: label.startCharIndex,
                            end: label.endCharIndex,
                            entityId: label.entityId
                        }))
                },
                toastData: {}
            });

            logButtonClick(serviceName, 'DataLabeling', 'RejectAllAutolabels', null, currentProjectId);
        }

        if (currentDocumentGptData?.entityLabels.length) {
            dispatcher.dispatch('rejectGptLabels', { labels: currentDocumentGptData.entityLabels, toastData: {} });
            logButtonClick(serviceName, 'DataLabeling', 'RejectAllGPTlabels', null, currentProjectId);
        }

        if (currentDocumentGptData?.classLabels.length) {
            dispatcher.dispatch('rejectGptClassificationLabels', {
                classIds: currentDocumentGptData?.classLabels.map(c => c.id),
                toastData: {}
            });
            logButtonClick(serviceName, 'DataLabeling', 'RejectAllGPTlabels', null, currentProjectId);
        }
    };

    const handleLoadNextBatchOfDocuments = (tracker?: ProgressTrackerStore) => {
        dispatcher.dispatch('getCustomTextNextBatchOfDocuments', {
            tracker,
            toastData: {
                running: { title: 'Loading next patch of files..' },
                completed: { title: 'Loading complete', content: 'The next batch was loaded successfully' }
            }
        });
    };

    const handleOnDiscardChanges = React.useCallback(() => {
        dispatcher.dispatch('resetStoreToExportedData', {});
    }, [dispatcher]);

    const handleLoadSpecificBatchDocuments = (fileName: string, tracker?: ProgressTrackerStore) => {
        dispatcher.dispatch('getCustomTextSpecificBatchWithPrefix', { prefix: fileName, tracker });
    };

    const onNextDocument = () => {
        if (samplerStore.isNextDisabled && currentProjectStore.nextBatchExists) {
            handleLoadNextBatchOfDocuments(goToNextFileTracker);
        } else {
            samplerStore.next();
        }
    };

    const onLoadMoreFiles = () => {
        if (samplerStore.searchQuery) {
            handleLoadSpecificBatchDocuments(samplerStore.searchQuery, loadNextBatchTracker);
        } else {
            handleLoadNextBatchOfDocuments(loadNextBatchTracker);
        }
    };

    const documentView = (() => {
        const documentNotFound = !documents.find(document => document.name === decodedDocumentName);

        if (isFetchingDocumentFailed) {
            return <EmptyStateView message={localize(messages.notFoundFileSubTitle, { name: decodedDocumentName })} />;
        }

        if (isLoading || goToNextFileTracker.isRunning || getSpecificFileTracker.isRunning) {
            return <DocumentLoadingView />;
        }

        if (decodedDocumentName && documentNotFound) {
            if (getSpecificFileTracker.isNotStarted && currentProjectStore.nextBatchExists) {
                handleLoadSpecificBatchDocuments(decodedDocumentName, getSpecificFileTracker);
            }

            if (getSpecificFileTracker.isCompleted || isFetchingDocumentFailed) {
                return <EmptyStateView message={localize(messages.notFoundFileSubTitle, { name: decodedDocumentName })} />;
            }
        }

        if (samplerStore.noDocumentsAvailable) {
            return samplerStore.areFiltersApplied ? (
                <ErrorTextContainer title={localize(messages.noDocumentsMatchingFilterTitle)} subTitle={''} />
            ) : (
                <EmptyStateView message={localize(messages.noDocumentsFound)} />
            );
        }

        if (!documents.length) {
            return (
                <ErrorTextContainer
                    subTitle={localize(messages.emptyStorageSubTitle)}
                    title={localize(messages.emptyStorageTitle, { containerName: currentProjectStore.containerName })}
                />
            );
        }

        if (samplerStore.currentDocument) {
            return (
                <DocumentView
                    labelingMode={labelingMode}
                    documentCulture={documentCulture}
                    isDocumentLoading={isCurrentDocumentLoading}
                    labelingHistoryStacks={labelingHistoryStacks}
                    onInvalidSelection={() => setIsLabelingWarningVisible(true)}
                    isExtractionLabelerDisabled={isLabelingLocked || currentProjectStore.projectKind !== 'CustomEntityRecognition'}
                />
            );
        }
    })();

    const getWarningMessages = () => (
        <Stack>
            {samplerStore.areFiltersApplied && (
                <>
                    <MessageBar
                        isMultiline
                        dismissButtonAriaLabel="Close"
                        styles={{ root: { width: '100%' } }}
                        messageBarType={MessageBarType.warning}>
                        {localize(messages.disabledAutoSavingWarning)}
                    </MessageBar>
                    <Announced aria-live="assertive" message={localize(messages.disabledAutoSavingWarning)} />
                </>
            )}
            {isLabelingWarningVisible && (
                <>
                    <MessageBar
                        isMultiline
                        dismissButtonAriaLabel="Close"
                        onDismiss={() => setIsLabelingWarningVisible(false)}
                        styles={{ root: { width: '100%' } }}
                        messageBarType={MessageBarType.warning}>
                        {localize(messages.labelingLimitWarning, { threshold: LuisConstants.CustomText.MaximumNumberOfLabelChars })}
                    </MessageBar>
                    <Announced aria-live="assertive" message={localize(messages.disabledAutoSavingWarning)} />
                </>
            )}
        </Stack>
    );

    const returnToModelPerformancePage = () => {
        history.push(
            routeTable.customText.modelDetailsPage(
                currentProjectStore.id,
                currentProjectStore.projectKind,
                modelPerformanceInfo?.modelName
            ),
            modelPerformanceInfo
        );
    };

    const handleAutoLabelClick = () => {
        logButtonClick(serviceName, 'DataLabeling', `TrigerAutoLabel`, null, currentProjectId);
        setIsAutotagTriggerModalHidden(false);
    };

    const getModelPerformanceInfoMessage = () => (
        <MessageBar
            isMultiline
            dismissButtonAriaLabel="Close"
            messageBarType={MessageBarType.info}
            styles={ModelPerformanceMessageBarStyles}
            onDismiss={() => setIsModelPerformanceMessageHidden(true)}>
            <Stack styles={{ root: { width: '100%' } }} horizontal={isXXLargeOrGreater} horizontalAlign="space-between">
                <LuisText>{localize(messages.modelPerformanceMessage)}</LuisText>
                <DefaultButton
                    onClick={returnToModelPerformancePage}
                    text={localize(messages.modelPerformanceButton)}
                    ariaLabel={localize(messages.modelPerformanceButton)}
                />
            </Stack>
        </MessageBar>
    );

    const subtitle = FeatureToggles.AMLIntegration ? (
        <LuisText variant="medium">
            {localize(
                isExtractionProject
                    ? messages.extractionSubTitle
                    : isSingleLabelClassificationProject
                    ? messages.singleClassSubTitle
                    : messages.multiClassSubTitle
            )}
            &nbsp;
            <Link styles={{ root: { textDecoration: 'underline' } }} onClick={() => setTaggingPaletteOpenPivot('azureML')}>
                {localize(messages.useAmlToLabel)}
            </Link>
            &nbsp;
            <LuisText>{localize(messages.subTitleCont)}</LuisText>
            &nbsp;
            <Link
                fontWeight={FontWeights.semibold as Property.FontWeight}
                styles={{ root: { textDecoration: 'underline' } }}
                onClick={() => history.push(routeTable.customText.trainingPage())}>
                {localize(messages.trainingLink)}.
            </Link>
        </LuisText>
    ) : (
        <LuisText variant="medium">
            {localize(
                isExtractionProject
                    ? messages.extractionSubTitleTemp
                    : isSingleLabelClassificationProject
                    ? messages.singleClassSubTitleTemp
                    : messages.multiClassSubTitleTemp
            )}
            &nbsp;
            <Link onClick={() => history.push(routeTable.customText.trainingPage())}>{localize(messages.trainingLink)}</Link>
        </LuisText>
    );

    const paletteContainer = (
        <PaletteContainer
            minWidth={paletteMinWidth}
            isCollapsed={isTaggingPaletteHeaderCollapsed}
            width={isMediumOrGreater ? tableAndPaletteWidthPercentage.paletteWidth : '100%'}>
            {isExtractionProject ? (
                <EntityPalette
                    entities={entities}
                    isLoading={isLoading}
                    onAddEntity={handleAddEntity}
                    projectId={currentProjectStore.id}
                    onDeleteEntity={handleDeleteEntity}
                    id={customTextPaletteIdRef.current}
                    openPivot={taggingPaletteOpenPivot}
                    gptStoreHouse={gptPredictionsStore}
                    footerElement={taggingPaletteFooter}
                    headerElement={taggingPaletteHeader}
                    isAddEntityDisabled={!documents.length}
                    onRenderAzureMLTab={onRenderAzureMLTab}
                    checkDeleteEntity={handleDeleteAmlWarning}
                    tagsPivotHeaderElement={TrainingLimitsHeader}
                    labelsCount={currentProjectStore?.labelsCount}
                    onOpenPivotChange={setTaggingPaletteOpenPivot}
                    addEntityButtonId={addEntityButtonIdRef.current}
                    autoLabelButtonId={autoLabelButtonIdRef.current}
                    onRenderRecommendationTab={onRenderRecommendationsTab}
                    onUnselectEntity={() => setLabelingMode({ mode: 'menu' })}
                    onRenderDistributionTab={onRenderTaggingPaletteDistributionTab}
                    isTriggerAutoTaggingJobDisabled={isTriggerAutoTaggingJobDisabled}
                    maxEntityNameCharLength={LuisConstants.CustomText.maxNamesCharLength}
                    onSelectEntity={entityId => setLabelingMode({ mode: 'brush', entityId })}
                    selectedEntityId={labelingMode.mode === 'brush' && labelingMode.entityId}
                    isBrushDisabled={isLabelingLocked || samplerStore.viewingOption === 'all'}
                    OnAutoLabelClick={FeatureToggles.CustomTextGptLabeling ? handleAutoLabelClick : null}
                />
            ) : (
                <CustomTextClassPalette
                    classes={classes}
                    isLoading={isLoading}
                    onAddClass={handleAddClass}
                    onDeleteClass={handleDeleteClass}
                    id={customTextPaletteIdRef.current}
                    openPivot={taggingPaletteOpenPivot}
                    footerElement={taggingPaletteFooter}
                    headerElement={taggingPaletteHeader}
                    onSubmitClasses={handleSubmitClasses}
                    isAddClassDisabled={!documents.length}
                    classLabels={getSelectedClassLabels()}
                    onRenderAzureMLTab={onRenderAzureMLTab}
                    checkDeleteClass={handleDeleteAmlWarning}
                    projectKind={currentProjectStore.projectKind}
                    tagsPivotHeaderElement={TrainingLimitsHeader}
                    onOpenPivotChange={setTaggingPaletteOpenPivot}
                    autoLabelButtonId={autoLabelButtonIdRef.current}
                    onRenderRecommendationTab={onRenderRecommendationsTab}
                    onRenderDistributionTab={onRenderTaggingPaletteDistributionTab}
                    isTriggerAutoTaggingJobDisabled={isTriggerAutoTaggingJobDisabled}
                    gptStoreHouse={samplerStore.viewingOption === 'single' && gptPredictionsStore}
                    OnAutoLabelClick={FeatureToggles.CustomTextGptLabeling ? handleAutoLabelClick : null}
                    isTaggingDisabled={isLabelingLocked || (samplerStore.viewingOption === 'all' && selectedItems.length === 0)}
                />
            )}
        </PaletteContainer>
    );

    return (
        <Page
            pageName="CustomTextDataTaggingPage"
            title={localize(messages.pageTitle)}
            reactToZoomLevel={false}
            data-id="CustomTextDataTagging">
            <ContentWrapper horizontal grow>
                {samplerStore.viewingOption === 'single' && (
                    <FilesViewer
                        isLoading={isLoading}
                        onSelect={navigateToDocument}
                        query={samplerStore.searchQuery}
                        handleLoadMoreFiles={onLoadMoreFiles}
                        selectedDocument={decodedDocumentName}
                        isExtractionProject={isExtractionProject}
                        documents={samplerStore.filteredDocuments}
                        appliedFilters={samplerStore.appliedFilters}
                        onChangeAppliedFilters={onChangeAppliedFilters}
                        areFiltersApplied={samplerStore.areFiltersApplied}
                        setQuery={query => samplerStore.setSearchQuery(query)}
                        isLoadingMoreFiles={loadNextBatchTracker.isRunning || goToNextFileTracker.isRunning}
                        showLoadMoreFiles={currentProjectStore.nextBatchExists && !samplerStore.areFiltersApplied}
                        taggedDocumentNames={documents.filter(d => d.classLabels.length || d.entityLabels.length).map(d => d.name)}
                        gptTaggedDocumentNames={gptPredictionsStore.documents
                            .filter(d => d.classLabels.length || d.entityLabels.length)
                            .map(d => d.name)}
                    />
                )}
                <Stack
                    grow
                    tokens={{ padding: '0 0 0 20px', childrenGap: 10 }}
                    styles={{ root: { width: tableAndPaletteWidthPercentage.tableWidth } }}>
                    <ScrollablePaneWrapper>
                        <ScrollablePane styles={containerScrollablePaneStyles}>
                            <Sticky stickyBackgroundColor="white" stickyPosition={StickyPositionType.Header}>
                                <CustomTextBreadcrumb />
                            </Sticky>
                            {isLabelingLocked ? (
                                <MessageBar messageBarType={MessageBarType.warning} styles={{ root: { minHeight: 'auto' } }}>
                                    <LuisText>
                                        {localize(messages.labelDisabledMessageBar)}
                                        &nbsp;
                                        <ExternalLink onClick={() => setTaggingPaletteOpenPivot('azureML')}>
                                            {localize(messages.labelDisabledMessageBarExtLink)}
                                        </ExternalLink>
                                        &nbsp;
                                        {localize(messages.labelDisabledMessageBarCont)}
                                    </LuisText>
                                </MessageBar>
                            ) : null}
                            <Stack tokens={{ childrenGap: 5 }}>
                                <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 5 }}>
                                    <EditableTitle title={localize(messages.pageTitle)} />
                                    {!isLoading && <SavingIndicator savingState={savingState} onDiscard={handleOnDiscardChanges} />}
                                    {!isMediumOrGreater && isTaggingPaletteHeaderCollapsed && (
                                        <Stack horizontalAlign="end" grow>
                                            <IconButton
                                                styles={collapseButtonStyles}
                                                aria-label={localize(messages.expandTaggingPalette)}
                                                iconProps={{ iconName: 'DoubleChevronLeftMed', styles: collapseIconStyles }}
                                                onClick={() => setIsTaggingPaletteHeaderCollapsed(!isTaggingPaletteHeaderCollapsed)}
                                            />
                                        </Stack>
                                    )}
                                </Stack>
                                {subtitle}
                            </Stack>

                            {samplerStore.viewingOption === 'all' ? (
                                <AllFilesView
                                    onSave={onSave}
                                    selection={selection}
                                    savingState={savingState}
                                    query={samplerStore.searchQuery}
                                    onDocumentClick={navigateToDocument}
                                    handleLoadMoreFiles={onLoadMoreFiles}
                                    onRenderWarnings={getWarningMessages}
                                    onChangeFilter={onChangeAppliedFilters}
                                    isExtractionProject={isExtractionProject}
                                    documents={samplerStore.filteredDocuments}
                                    onFileViewChange={onChangeViewingOption}
                                    appliedFilters={samplerStore.appliedFilters}
                                    setQuery={query => samplerStore.setSearchQuery(query)}
                                    isLoadingMoreFiles={loadNextBatchTracker.isRunning || goToNextFileTracker.isRunning}
                                    showLoadMoreFiles={currentProjectStore.nextBatchExists && !samplerStore.areFiltersApplied}
                                />
                            ) : (
                                <Stack grow styles={{ root: { minHeight: 500 } }}>
                                    <Stack.Item>
                                        <SamplingCommandBar
                                            isLoading={isLoading}
                                            documentCulture={documentCulture}
                                            isInEmptyState={!documents.length}
                                            onAcceptAllAutoTags={handleAcceptAllAutoTags}
                                            onRejectAllAutoTags={handleRejectAllAutoTags}
                                            documentId={samplerStore.currentDocument ?? '-'}
                                            onChangeDocumentCulture={handleDocumentCultureChange}
                                            areAutoTagsControlsDisabled={areAutoTagsControlsDisabled}
                                            areAutoTagsControlsHidden={isSingleLabelClassificationProject}
                                            isCultureDropdownEnabled={samplerStore.currentDocument && currentProjectStore.multiLingual}
                                            onChangeViewingOption={onChangeViewingOption}
                                        />
                                        {getWarningMessages()}
                                    </Stack.Item>

                                    {!isModelPerformanceMessageHidden && <Stack.Item>{getModelPerformanceInfoMessage()}</Stack.Item>}

                                    {documentView}
                                    <SamplingFooter
                                        onSave={() => {
                                            onSave();
                                            logButtonClick(serviceName, 'DataLabeling', 'SaveLabels', null, currentProjectId);
                                        }}
                                        savingState={savingState}
                                        onNext={() => {
                                            onNextDocument();
                                            logButtonClick(serviceName, 'DataLabeling', 'NextDoc', null, currentProjectId);
                                        }}
                                        onBack={() => {
                                            samplerStore.previous();
                                            logButtonClick(serviceName, 'DataLabeling', 'PreviousDoc', null, currentProjectId);
                                        }}
                                        isBackDisabled={samplerStore.isPreviousDisabled}
                                        areNavButtonsDisplayed={samplerStore.viewingOption === 'single'}
                                        isNextDisabled={samplerStore.isNextDisabled && samplerStore.areFiltersApplied}
                                    />
                                </Stack>
                            )}
                        </ScrollablePane>
                    </ScrollablePaneWrapper>
                </Stack>

                {isMediumOrGreater ? (
                    <>{paletteContainer}</>
                ) : (
                    <Panel
                        type={PanelType.smallFluid}
                        isOpen={!isTaggingPaletteHeaderCollapsed}
                        onDismiss={() => setIsTaggingPaletteHeaderCollapsed(true)}>
                        {paletteContainer}
                    </Panel>
                )}
            </ContentWrapper>

            <BasicModal
                onSubmit={onSave}
                valid={savingState === 'dirty'}
                doneButtonText={localize(messages.saveNow)}
                onDismiss={() => setIsChangeFilterPromptHidden(true)}
                hidden={isChangeFilterPromptHidden}
                dialogContentProps={{
                    title: localize(messages.changeFilterPromptTitle),
                    subText: localize(messages.changeFilterPromptBody)
                }}
            />

            <AutoLabelingModal
                hidden={isAutotagTriggerModalHidden}
                onSubmit={() => setIsAutotagTriggerModalHidden(true)}
                onDismiss={() => setIsAutotagTriggerModalHidden(true)}
            />

            <BasicModal
                minWidth={416}
                onDismiss={() => setDeleteWarningModalHidden(true)}
                hidden={deleteWarningModalHidden}
                modalProps={{ isModeless: true }}
                shouldRenderFooter={false}
                dialogContentProps={{
                    title: isExtractionProject
                        ? localize(messages.nerDeleteWarningModalTitle)
                        : localize(messages.classificationDeleteWarningModalTitle)
                }}>
                <Stack tokens={{ padding: 10, childrenGap: 40 }}>
                    <Stack>
                        <LuisText>
                            {isExtractionProject
                                ? localize(messages.nerDeleteWarningModalContent)
                                : localize(messages.classificationDeleteWarningModalContent)}
                        </LuisText>
                        <br />
                    </Stack>
                    <Stack tokens={{ childrenGap: 10 }} horizontal horizontalAlign="end">
                        <PrimaryButton text={localize(messages.gotIt)} onClick={() => setDeleteWarningModalHidden(true)} />
                    </Stack>
                </Stack>
            </BasicModal>
        </Page>
    );
});
